import React from "react";
import { Image, Pressable, Text, View } from "react-native";
import { Check, UploadCloud, X } from "lucide-react-native";
import {
  PrimaryButton,
  Row,
  SecondaryButton,
  SupportingSubtitleText,
  Tag,
} from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { EMBER } from "../../../onboarding/theme";
import { MedFormIcon } from "../care/rows/MedRow";
import { DocumentRow } from "../documents/DocumentRow";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import {
  ScanTileButton,
  type ConciergePrescriptionFile,
  type ExtractedMedication,
  type QuoteRequestStatus,
} from "./pharmacyConciergeShared";

export type {
  ConciergePrescriptionFile,
  ExtractedMedication,
  QuoteRequestStatus,
} from "./pharmacyConciergeShared";

// Mirrors DocumentProcessingActivity's local FileTypeIcon: uses the 3D upload
// glyphs the rest of the app uses for document rows.
const uploadFileIcons = {
  document:
    require("@src/apps/moon/assets/images/upload-file-icons/document-3d.png") as number,
  image:
    require("@src/apps/moon/assets/images/upload-file-icons/image-3d.png") as number,
  generic:
    require("@src/apps/moon/assets/images/upload-file-icons/generic-3d.png") as number,
} as const;

function fileIconKind(fileName: string): keyof typeof uploadFileIcons {
  const ext = fileName.split(".").pop()?.toLowerCase();
  if (ext && ["jpg", "jpeg", "png", "gif", "webp", "heic"].includes(ext)) {
    return "image";
  }
  if (ext && ["pdf", "doc", "docx", "txt"].includes(ext)) return "document";
  return "generic";
}

function FileTypeIcon({ fileName }: { fileName: string }) {
  return (
    <Image
      accessibilityLabel={`${fileIconKind(fileName)} file`}
      source={uploadFileIcons[fileIconKind(fileName)]}
      resizeMode="contain"
      style={{ width: 34, height: 34 }}
    />
  );
}

type Props = {
  /** The Rx on file — a fresh pick, or the upload the backend request cites. */
  file: Pick<ConciergePrescriptionFile, "name"> | null;
  extractedMedications: ExtractedMedication[];
  isExtracting: boolean;
  extractionFailed: boolean;
  quoteRequestStatus: QuoteRequestStatus;
  /** True until the backend draft exists for this Rx (upload still saving). */
  requestQuotesDisabled?: boolean;
  onUploadPress: () => void;
  onReplacePress: () => void;
  onRemovePress: () => void;
  onRequestQuotesPress: () => void;
  onOpenQuotesTabPress: () => void;

  // Labels
  titleLabel: string;
  emptyBodyLabel: string;
  uploadCtaLabel: string;
  filledBodyLabel: string;
  detectedMedsEmpty: string;
  detectedMedsProcessing: string;
  detectedMedsFailed: string;
  replaceLabel: string;
  removeLabel: string;
  requestQuotesLabel: string;
  inProgressTitle: string;
  inProgressBody: string;
  inProgressCta: string;
  doneTitle: string;
  doneBody: string;
};

/**
 * "Your prescription" section on top of the Prescription tab. Renders the Rx
 * side of the concierge workflow: empty (upload CTA) or filled (file preview
 * + extracted meds + Get-quotes CTA). Once the concierge request is sent, the
 * CTA becomes a status Row pointing the patient at the Quotes tab where they
 * can upload the quotes pharmacies email back to them.
 */
export function PharmacyPrescriptionSection({
  file,
  extractedMedications,
  isExtracting,
  extractionFailed,
  quoteRequestStatus,
  requestQuotesDisabled = false,
  onUploadPress,
  onReplacePress,
  onRemovePress,
  onRequestQuotesPress,
  onOpenQuotesTabPress,
  titleLabel,
  emptyBodyLabel,
  uploadCtaLabel,
  filledBodyLabel,
  detectedMedsEmpty,
  detectedMedsProcessing,
  detectedMedsFailed,
  replaceLabel,
  removeLabel,
  requestQuotesLabel,
  inProgressTitle,
  inProgressBody,
  inProgressCta,
  doneTitle,
  doneBody,
}: Props) {
  const hasActiveRequest = quoteRequestStatus !== "idle";
  const isDone = quoteRequestStatus === "done";

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        marginTop: 0,
        marginBottom: 16,
        padding: 18,
        gap: 14,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          color: EMBER.colors.text,
          fontSize: 17,
          fontWeight: "700",
        }}
      >
        {titleLabel}
      </Text>

      {file ? (
        <>
          <DocumentRow
            title={file.name}
            subtitle={
              isExtracting
                ? detectedMedsProcessing
                : extractionFailed
                  ? detectedMedsFailed
                  : undefined
            }
            imageSource={uploadFileIcons[fileIconKind(file.name)]}
            trailing={
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {isExtracting ? (
                  <ThinkingOrbStatus
                    accessibilityLabel={detectedMedsProcessing}
                    state="shaping"
                    size={20}
                    theme="light"
                  />
                ) : null}
                <Pressable
                  onPress={onRemovePress}
                  accessibilityRole="button"
                  accessibilityLabel={removeLabel}
                  hitSlop={10}
                  style={{ padding: 4 }}
                >
                  <X
                    size={16}
                    color={EMBER.colors.textLight}
                    strokeWidth={1.8}
                  />
                </Pressable>
              </View>
            }
            showChevron={false}
          />

          <ExtractedMedsBlock
            medications={extractedMedications}
            isExtracting={isExtracting}
            extractionFailed={extractionFailed}
            emptyLabel={detectedMedsEmpty}
          />

          {hasActiveRequest ? (
            <Row
              surface="plain"
              leading={
                isDone ? (
                  <Check size={20} color={EMBER.colors.text} strokeWidth={2} />
                ) : (
                  <ThinkingOrbStatus
                    accessibilityLabel={inProgressTitle}
                    state="shaping"
                    size={20}
                    theme="light"
                  />
                )
              }
              title={isDone ? doneTitle : inProgressTitle}
              subtitle={isDone ? doneBody : inProgressBody}
              subtitleVariant="meta"
              subtitleNumberOfLines={2}
              chevron
              onPress={onOpenQuotesTabPress}
              accessibilityLabel={inProgressCta}
              paddingVertical={14}
            />
          ) : (
            <>
              <Text
                className="font-dm-sans"
                style={{
                  color: EMBER.colors.textLight,
                  fontSize: 13,
                  lineHeight: 19,
                }}
              >
                {filledBodyLabel}
              </Text>

              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <PrimaryButton
                  label={requestQuotesLabel}
                  onPress={onRequestQuotesPress}
                  disabled={requestQuotesDisabled}
                  flex={2}
                />
                <SecondaryButton
                  label={replaceLabel}
                  action="access.pharmacy.prescription_section_replace"
                  onPress={onReplacePress}
                  flex={1}
                />
              </View>
            </>
          )}
        </>
      ) : (
        <ScanTileButton
          icon={UploadCloud}
          title={uploadCtaLabel}
          subtitle={emptyBodyLabel}
          action="access.pharmacy.prescription_section_upload_cta"
          onPress={onUploadPress}
        />
      )}
    </View>
  );
}

// Detected meds render as canonical `Row` primitives (leading MedFormIcon +
// title + dosage Tag), matching how the medication schedule renders in Care.
// The extraction loading state lives on the file row's trailing ThinkingOrb,
// so this block is empty during extraction — nothing to duplicate.
function ExtractedMedsBlock({
  medications,
  isExtracting,
  extractionFailed,
  emptyLabel,
}: {
  medications: ExtractedMedication[];
  isExtracting: boolean;
  extractionFailed: boolean;
  emptyLabel: string;
}) {
  if (isExtracting || extractionFailed) return null;

  if (medications.length === 0) {
    return (
      <SupportingSubtitleText style={{ marginTop: 0 }}>
        {emptyLabel}
      </SupportingSubtitleText>
    );
  }

  return (
    <View>
      {medications.map((med, idx) => (
        <Row
          key={`${med.name}-${idx}`}
          surface="plain"
          density="dense"
          leading={<MedFormIcon form="vial" size={28} />}
          leadingSize={28}
          title={med.name}
          titleNumberOfLines={1}
          titleRight={
            med.dosage ? <Tag label={med.dosage} tone="neutral" /> : undefined
          }
          paddingHorizontal={0}
          paddingVertical={6}
        />
      ))}
    </View>
  );
}
